/* eslint-disable react/no-danger, jsx-a11y/no-noninteractive-element-interactions */
import * as React from 'react';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import classnames from 'classnames';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { ChevronDownIcon, ChevronUpIcon } from '@fluentui/react-icons';
import { useSelector } from 'react-redux';
import { betreuerByNrSelector, lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { createKundeUrl, createRecordUrl, formatMoney, formatPercent, formatSmDate } from 'utils/formatter';
import { AuftragStatusLabel } from 'utils/labels';
import { FilterValues, LineData } from './types';
import { FilterInfo } from './FilterInfo';
import { useOffeneAuftraegeSummary } from './hooks/useOffeneAuftraegeSummary';

const theme: ITheme = getTheme();
const { palette } = theme;

const odd = palette.neutralLight;
const even = palette.neutralLighterAlt;

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    header: {
        marginTop: 35,
    },
    printHeader: {},
    tdHead: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        cursor: 'pointer',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdBody: {
        paddingLeft: 2,
        paddingRight: 2,
        borderBottom: '0.5px solid #CCC',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },

    borderTop: {
        borderTop: '1px solid',
    },
    strongBorderTop: {
        borderTop: '3px solid',
    },
    borderBottom: {
        borderBottom: '1px solid',
    },
    strongBorderBottom: {
        borderBottom: '3px solid',
    },
    noPadding: {
        paddingTop: 0,
        paddingBottom: 0,
    },
    auNr: {},
    datum: {},
    text: {},
    ekNetto: {
        backgroundColor: even,
    },
    vkNetto: {
        backgroundColor: odd,
    },
    bMarge: {
        backgroundColor: even,
    },
    anzahlLieferungen: {
        backgroundColor: odd,
    },
    letzteLieferung: {
        backgroundColor: odd,
    },
    datBis: {
        backgroundColor: even,
    },
    borderRight: {
        borderRight: '1px solid',
    },
    note: {
        fontSize: 12,
        color: palette.neutralTertiary,
    },
});

interface OffeneAuftraegeProps extends FilterValues {
    printView?: boolean;
    toggleSort: (column: string) => void;
}

export const OffeneAuftraege: React.FC<OffeneAuftraegeProps> = (filterValues) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);

    const betreuerByNr = useSelector(betreuerByNrSelector);

    const { printView = false, sortColumn, sortDirection, toggleSort } = filterValues;

    const offeneAuftraegeSummary = useOffeneAuftraegeSummary(filterValues);

    const sortedLineDataList = React.useMemo<LineData[]>(() => {
        if (sortColumn === 'status') {
            return offeneAuftraegeSummary.lineDataList.sort((a, b) =>
                sortDirection === 'asc'
                    ? (AuftragStatusLabel[a.status] || '')
                          .toLocaleLowerCase()
                          .localeCompare(AuftragStatusLabel[b.status] || '')
                    : (AuftragStatusLabel[b.status] || '')
                          .toLocaleLowerCase()
                          .localeCompare(AuftragStatusLabel[a.status] || ''),
            );
        }

        if (['auNr', 'text', 'datum', 'datBis', 'letzteLieferung'].includes(sortColumn)) {
            return offeneAuftraegeSummary.lineDataList.sort((a, b) =>
                sortDirection === 'asc'
                    ? ((a[sortColumn as keyof LineData] as string) || '')
                          .toLocaleLowerCase()
                          .localeCompare((b[sortColumn as keyof LineData] as string) || '')
                    : ((b[sortColumn as keyof LineData] as string) || '')
                          .toLocaleLowerCase()
                          .localeCompare((a[sortColumn as keyof LineData] as string) || ''),
            );
        }

        return offeneAuftraegeSummary.lineDataList.sort((a, b) =>
            sortDirection === 'asc'
                ? ((a[sortColumn as keyof LineData] as number) || 0) -
                  ((b[sortColumn as keyof LineData] as number) || 0)
                : ((b[sortColumn as keyof LineData] as number) || 0) -
                  ((a[sortColumn as keyof LineData] as number) || 0),
        );
    }, [offeneAuftraegeSummary.lineDataList, sortColumn, sortDirection]);

    const renderSortIcon = (column: string) => {
        if (sortColumn === column) {
            if (sortDirection === 'asc') {
                return <ChevronUpIcon className={formStyles.reportSortIcon} />;
            }
            return <ChevronDownIcon className={formStyles.reportSortIcon} />;
        }
        return null;
    };

    const renderHeaderRow = () => {
        const moneyClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
        );

        return (
            <tr>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportAuNrPrintCell : formStyles.reportAuNrCell,
                    )}
                    onClick={() => toggleSort('auNr')}
                >
                    AuftragNr.
                    {renderSortIcon('auNr')}
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        formStyles.dataValueRight,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                    onClick={() => toggleSort('datum')}
                >
                    {renderSortIcon('datum')}
                    Datum
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                    )}
                    onClick={() => toggleSort('text')}
                >
                    Kunde
                    {renderSortIcon('text')}
                </td>
                <td
                    className={classnames(
                        styles.tdHead,
                        styles.borderRight,
                        printView ? formStyles.reportAuftragStatusPrintCell : formStyles.reportAuftragStatusCell,
                    )}
                    onClick={() => toggleSort('status')}
                >
                    Status
                    {renderSortIcon('status')}
                </td>
                <td className={classnames(moneyClassName, styles.vkNetto)} onClick={() => toggleSort('vkNettoGesamt')}>
                    {renderSortIcon('vkNettoGesamt')}
                    VK Netto
                    <br />
                    Gesamt
                </td>
                <td className={classnames(moneyClassName, styles.vkNetto)} onClick={() => toggleSort('vkNettoOffenP')}>
                    {renderSortIcon('vkNettoOffenP')}%
                </td>
                <td
                    className={classnames(moneyClassName, styles.vkNetto, styles.borderRight)}
                    onClick={() => toggleSort('vkNettoOffen')}
                >
                    {renderSortIcon('vkNettoOffen')}
                    VK Netto
                    <br />
                    Offen
                </td>
                <td className={classnames(moneyClassName, styles.bMarge)} onClick={() => toggleSort('bMargeGesamt')}>
                    {renderSortIcon('bMargeGesamt')}
                    B-Marge
                    <br />
                    Gesamt
                </td>
                <td className={classnames(moneyClassName, styles.ekNetto)} onClick={() => toggleSort('bMargeOffenP')}>
                    {renderSortIcon('bMargeOffenP')}%
                </td>
                <td
                    className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}
                    onClick={() => toggleSort('bMargeOffen')}
                >
                    {renderSortIcon('bMargeOffen')}
                    B-Marge
                    <br />
                    Offen
                </td>
                <td
                    className={classnames(moneyClassName, styles.anzahlLieferungen)}
                    onClick={() => toggleSort('anzahlLieferungen')}
                >
                    {renderSortIcon('anzahlLieferungen')}
                    Anzahl Lieferungen
                </td>
                <td
                    className={classnames(
                        moneyClassName,
                        styles.letzteLieferung,
                        formStyles.dataValueRight,
                        styles.borderRight,
                    )}
                    onClick={() => toggleSort('letzteLieferung')}
                >
                    {renderSortIcon('letzteLieferung')}
                    Letzte Lieferung
                </td>
            </tr>
        );
    };

    const moneyClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );

    const percentClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportPercentSmallPrintCell : formStyles.reportPercentSmallCell,
    );

    const renderLine = (line: LineData, key?: string, text?: string, hideStatus = false) => {
        return (
            <tr key={key || line.auNr}>
                <td className={styles.tdBody}>
                    {!printView ? (
                        <OpenLink link={createRecordUrl('auftraege', line.kNr, line.auNr)} label={line.auNr} />
                    ) : (
                        <div>{line.auNr}</div>
                    )}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        formStyles.dataValueRight,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                >
                    {formatSmDate(line.datum, true)}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        printView ? formStyles.reportKundePrintCell : formStyles.reportKundeCell,
                    )}
                >
                    {!printView && line.kNr ? (
                        <OpenLink link={createKundeUrl(line.kNr)} label={text || line.text} />
                    ) : (
                        <div>{text || line.text}</div>
                    )}
                </td>
                <td
                    className={classnames(
                        styles.tdBody,
                        styles.borderRight,
                        printView ? formStyles.reportAuftragStatusPrintCell : formStyles.reportAuftragStatusCell,
                    )}
                >
                    {hideStatus ? null : AuftragStatusLabel[line.status] || line.status}
                </td>
                <td className={classnames(moneyClassName, styles.vkNetto)}>{formatMoney(line.vkNettoGesamt)}</td>
                <td className={classnames(percentClassName, styles.vkNetto)}>{formatPercent(line.vkNettoOffenP)}</td>
                <td className={classnames(moneyClassName, styles.vkNetto, styles.borderRight)}>
                    <strong>{formatMoney(line.vkNettoOffen)}</strong>
                </td>
                <td className={classnames(moneyClassName, styles.bMarge)}>{formatMoney(line.bMargeGesamt)}</td>
                <td className={classnames(percentClassName, styles.bMarge)}>{formatPercent(line.bMargeOffenP)}</td>
                <td className={classnames(moneyClassName, styles.bMarge, styles.borderRight)}>
                    {formatMoney(line.bMargeOffen)}
                </td>
                <td className={classnames(moneyClassName, styles.anzahlLieferungen)}>{line.anzahlLieferungen}</td>
                <td
                    className={classnames(
                        styles.tdBody,
                        styles.letzteLieferung,
                        formStyles.dataValueRight,
                        styles.borderRight,
                        printView ? formStyles.reportDatumPrintCell : formStyles.reportDatumCell,
                    )}
                >
                    {formatSmDate(line.letzteLieferung, true)}
                </td>
            </tr>
        );
    };

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {offeneAuftraegeSummary.loading && <LoadingIndicator />}
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            {!printView && (
                <table className={formStyles.reportFixedHeaderNoBgColor}>
                    <thead>{renderHeaderRow()}</thead>
                </table>
            )}
            <h1 className={printView ? styles.printHeader : styles.header}>Offene Aufträge</h1>
            {printView && <FilterInfo filterValues={filterValues} />}
            <table className={formStyles.reportTable}>
                <thead>{renderHeaderRow()}</thead>
                <tbody>
                    {sortedLineDataList.map((line) => renderLine(line))}
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                    {renderLine(
                        offeneAuftraegeSummary.total,
                        'total',
                        'Gesamt (' + offeneAuftraegeSummary.total.count + ')',
                    )}
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                    {Object.keys(offeneAuftraegeSummary.betreuerTotal).map((betreuer) =>
                        renderLine(
                            offeneAuftraegeSummary.betreuerTotal[betreuer],
                            betreuer,
                            (betreuerByNr[betreuer].name || betreuer) +
                                ' (' +
                                offeneAuftraegeSummary.betreuerTotal[betreuer].count +
                                ')',
                            true,
                        ),
                    )}
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                    {Object.keys(offeneAuftraegeSummary.telBetreuerTotal).map((betreuer) =>
                        renderLine(
                            offeneAuftraegeSummary.telBetreuerTotal[betreuer],
                            betreuer,
                            (betreuerByNr[betreuer].name || betreuer) +
                                ' (' +
                                offeneAuftraegeSummary.telBetreuerTotal[betreuer].count +
                                ')',
                            true,
                        ),
                    )}
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
